import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import mailOne from "../assets/mail04.jpeg";
import mailTwo from "../assets/mail01.jpeg";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
} from "react-icons/fa";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { getFormApiUrl } from "../utils/formApiUrl";

const ContactUs = () => {
  const settings = useSiteSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const emailBody = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { 
              background: linear-gradient(135deg, #d946ef 0%, #9333ea 100%); 
              color: white; 
              padding: 30px; 
              text-align: center; 
              border-radius: 10px 10px 0 0; 
            }
            .content { 
              background: #f9f9f9; 
              padding: 30px; 
              border: 1px solid #ddd; 
            }
            .field { 
              margin-bottom: 20px; 
              padding: 15px;
              background: white;
              border-radius: 8px;
            }
            .label { 
              font-weight: bold; 
              color: #9333ea; 
              margin-bottom: 8px;
              display: block;
            }
            .value { 
              color: #333;
              word-wrap: break-word;
            }
            .footer { 
              background: #333; 
              color: white; 
              padding: 20px; 
              text-align: center; 
              border-radius: 0 0 10px 10px; 
              font-size: 12px; 
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1 style="margin: 0;">New Contact Form Submission</h1>
              <p style="margin: 10px 0 0 0;">JRIIT Website Contact Form</p>
            </div>
            <div class="content">
              <div class="field">
                <span class="label">Name:</span>
                <span class="value">${formData.name}</span>
              </div>
              <div class="field">
                <span class="label">Email:</span>
                <span class="value"><a href="mailto:${formData.email}">${formData.email}</a></span>
              </div>
              <div class="field">
                <span class="label">Subject:</span>
                <span class="value">${formData.subject}</span>
              </div>
              <div class="field">
                <span class="label">Message:</span>
                <div class="value">${formData.message.replace(/\n/g, "<br>")}</div>
              </div>
            </div>
            <div class="footer">
              <p style="margin: 0;">This email was sent from the JRIIT website contact form</p>
              <p style="margin: 10px 0 0 0;">Received on: ${new Date().toLocaleString(
                "en-US",
                {
                  dateStyle: "full",
                  timeStyle: "long",
                  timeZone: "Africa/Dar_es_Salaam",
                },
              )}</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const response = await fetch(getFormApiUrl('send-contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: formData.subject,
          body: emailBody,
          replyTo: formData.email,
          replyToName: formData.name
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      } else {
        console.error("Contact Email Error:", result);
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Email send error:", error.response?.data || error.message);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  // All contact info now from CMS settings
  const heroTitle = settings.contactHeroTitle || "Get in Touch with Us";
  const heroSubtitle = settings.contactHeroSubtitle || "Have questions about our programs, admissions, or anything else? We'd love to hear from you. Reach out and we'll respond as soon as possible.";
  const formTitle = settings.contactFormTitle || "Let's talk";
  const formDescription = settings.contactFormDescription || "To request information about our programs or schedule a campus visit, contact us directly or fill out the form and we will get back to you promptly.";
  const address = settings.contactAddress || "Sakina Area Arusha Tanzania, P. O. Box 7071 Namanga Road";
  const phone1 = settings.contactPhone || "+255 754 360 590";
  const extraPhones = settings.contactPhone2 ? settings.contactPhone2.split('\n').filter(Boolean) : [];
  const emailPrincipal = settings.contactEmail || "principal@jriit.ac.tz";
  const emailAdmin = settings.contactEmailAdmin || "admin@jriit.ac.tz";
  const extraEmails = settings.contactEmailMarketing
    ? settings.contactEmailMarketing.split('\n').filter(Boolean).map(entry => {
        const colonIdx = entry.indexOf(':');
        if (colonIdx >= 0) {
          return { label: entry.substring(0, colonIdx), address: entry.substring(colonIdx + 1) };
        }
        return { label: 'Marketing', address: entry };
      })
    : [];
  const hoursWeekdays = settings.contactHours || "Monday - Friday: 8:00 AM - 5:00 PM";
  const hoursSaturday = settings.contactHoursSaturday || "Saturday: 9:00 AM - 2:00 PM";
  const hoursSunday = settings.contactHoursSunday || "Sunday: Closed";
  const mapTitle = settings.contactMapTitle || "Find Us on the Map";
  const mapSubtitle = settings.contactMapSubtitle || "Visit our campus and experience the JRIIT difference firsthand";
  const mapEmbedUrl = settings.contactMapEmbedUrl || "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15931.805561943684!2d36.660178!3d-3.362054!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18371c56dc7ed7ed%3A0xc47ab61515db909e!2sJr%20Institute%20of%20Information%20Technology!5e0!3m2!1ssw!2stz!4v1756765268608!5m2!1ssw!2stz";
  const ctaTitle = settings.contactCtaTitle || "Ready to Start Your Journey?";
  const ctaDescription = settings.contactCtaDescription || "Join thousands of students who have chosen JRIIT for their education. Contact us today to learn more about our programs and admission process.";
  const ctaBtn1Text = settings.contactCtaButton1Text || "Apply Now";
  const ctaBtn1Link = settings.contactCtaButton1Link || "/admissions";
  const ctaBtn2Text = settings.contactCtaButton2Text || "Learn More";
  const ctaBtn2Link = settings.contactCtaButton2Link || "/";

  const socialMedia = [
    { name: "WhatsApp", icon: FaWhatsapp, url: settings.whatsappUrl, color: "bg-green-600" },
    { name: "Facebook", icon: FaFacebook, url: settings.facebookUrl, color: "bg-blue-600" },
    { name: "Twitter", icon: FaTwitter, url: settings.twitterUrl, color: "bg-blue-400" },
    { name: "Instagram", icon: FaInstagram, url: settings.instagramUrl, color: "bg-gradient-to-r from-purple-500 to-pink-500" },
    { name: "LinkedIn", icon: FaLinkedin, url: settings.linkedinUrl, color: "bg-blue-700" },
    { name: "YouTube", icon: FaYoutube, url: settings.youtubeUrl, color: "bg-red-600" },
  ].filter((s) => s.url);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      <section className="relative py-40 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={mailOne}
            alt="JRIIT College Campus" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-custom-pink/90 via-purple-800/85 to-gold/80"></div>
        </div>
        
        <div className="absolute top-10 left-10 w-32 h-32 bg-gold/20 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-custom-pink/20 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>
        
        <div className="relative container mx-auto px-4 text-center z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 drop-shadow-2xl">
            {heroTitle.includes("Touch") ? (
              <>
                {heroTitle.split("Touch")[0]}<span className="text-gold drop-shadow-lg">Touch</span>{heroTitle.split("Touch")[1]}
              </>
            ) : (
              heroTitle
            )}
          </h1>
          <p className="text-xl md:text-2xl text-white/95 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
            {heroSubtitle}
          </p>
          <div className="w-32 h-1 bg-gold mx-auto mt-8 rounded-full shadow-lg"></div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {formTitle}
                </h2>
                <p className="text-gray-600 text-lg">
                  {formDescription}
                </p>
              </div>

              {submitStatus === "success" && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you! Your message has been sent successfully.
                </div>
              )}
              
              {submitStatus === "error" && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Sorry! There was an error sending your message. Please try
                  again or contact us directly at {emailPrincipal}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-pink transition-all duration-300 ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-pink transition-all duration-300 ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-pink transition-all duration-300 ${
                      errors.subject ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="What is this about?"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-pink transition-all duration-300 resize-none ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Type your message here..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-custom-pink to-purple-700 text-white py-4 px-8 rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-custom-pink transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? "Sending Message..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Contact Information & Social */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={mailTwo}
                    alt="JRIIT College Contact Information" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-custom-pink/40 via-purple-500/40 to-gold/40"></div>
                  <div className="absolute top-4 right-4 w-16 h-16 bg-gold/20 rounded-full blur-md"></div>
                  <div className="absolute bottom-4 left-4 w-20 h-20 bg-custom-pink/20 rounded-full blur-md"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                      <div className="w-16 h-16 bg-gradient-to-br from-custom-pink to-purple-600 rounded-full flex items-center justify-center">
                        <FaEnvelope className="text-white text-3xl" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Contact Information
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Address */}
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-custom-pink/10 rounded-full flex items-center justify-center">
                        <FaMapMarkerAlt className="text-custom-pink text-xl" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Address
                        </h4>
                        <p className="text-gray-600">{address}</p>
                      </div>
                    </div>

                    {/* Phone Numbers */}
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center">
                        <FaPhone className="text-gold text-xl" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Phone Numbers
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <p>
                            <span className="font-medium">Tel 1:</span>{" "}
                            {phone1}
                          </p>
                          {extraPhones.map((phone, idx) => (
                            <p key={idx}>
                              <span className="font-medium">Tel {idx + 2}:</span>{" "}
                              {phone}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Email Addresses */}
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                        <FaEnvelope className="text-purple-500 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Email Addresses
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <p>
                            <span className="font-medium">Principal:</span>{" "}
                            {emailPrincipal}
                          </p>
                          <p>
                            <span className="font-medium">Admin:</span>{" "}
                            {emailAdmin}
                          </p>
                          {extraEmails.map((entry, idx) => (
                            <p key={idx}>
                              <span className="font-medium">{entry.label}:</span>{" "}
                              {entry.address}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Working Hours */}
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                        <FaClock className="text-green-500 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Working Hours
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <p>{hoursWeekdays}</p>
                          <p>{hoursSaturday}</p>
                          <p>{hoursSunday}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              {socialMedia.length > 0 && (
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Follow Us
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    {socialMedia.map((social) => (
                      <Link
                        key={social.name}
                        to={social.url}
                        className={`w-12 h-12 ${social.color} rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300`}
                        aria-label={`Follow us on ${social.name}`}
                      >
                        <social.icon className="text-xl" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {mapTitle}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {mapSubtitle}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <iframe 
              src={mapEmbedUrl}
              width="100%" 
              height="400" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="JRIIT College Location"
              className="w-full h-96"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {ctaTitle}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to={ctaBtn1Link}
              className="bg-gold text-black px-8 py-4 rounded-full font-semibold hover:bg-yellow-400 transition-colors duration-300 transform hover:scale-105"
            >
              {ctaBtn1Text}
            </Link>
            <Link
              to={ctaBtn2Link}
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-custom-pink transition-all duration-300 transform hover:scale-105"
            >
              {ctaBtn2Text}
            </Link>
          </div>
        </div>
      </section>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default ContactUs;
